import HttpService from '../../../services/http/httpService';
import config from '../../../config';

// Servicio del modulo de tickets (helpdesk). Extiende HttpService, que ya
// inyecta el Bearer token y el userEmail en cada peticion.
//
// Convencion de headquarter (CEDESCA|CNTEC):
//  - GET / DELETE -> en query params
//  - POST / PATCH -> en el body
class TicketsService extends HttpService {
  /* ---- Catalogos (desplegables) ---- */

  // GET /tickets/usuarios -> [{ id, usuario, nombre, email }] (agentes, sin admin)
  async listarUsuarios({ headquarter }) {
    return this.get('tickets/usuarios', { params: { headquarter } });
  }

  // GET /tickets/estados -> [{ id, descripcion }]
  async listarEstados({ headquarter }) {
    return this.get('tickets/estados', { params: { headquarter } });
  }

  // GET /tickets/grupos -> [{ id, nombre, descripcion }] (solo activos)
  async listarGrupos({ headquarter }) {
    return this.get('tickets/grupos', { params: { headquarter } });
  }

  // GET /tickets/temas -> [{ id, nombre }] (solo activos)
  async listarTemas({ headquarter }) {
    return this.get('tickets/temas', { params: { headquarter } });
  }

  // GET /tickets/etiquetas -> [{ id, nombre }]
  async listarEtiquetas({ headquarter }) {
    return this.get('tickets/etiquetas', { params: { headquarter } });
  }

  /* ---- Tickets ---- */

  // GET /tickets -> { total, page, pageSize, data: [...] }
  async listarTickets({ headquarter, page, pageSize, ...filtros }) {
    return this.get('tickets', {
      params: { headquarter, page, pageSize, ...filtros },
    });
  }

  // GET /tickets/:id -> { ...ticket, estado, grupo, tema, agente, etiquetas: [...] }
  async obtenerTicket({ headquarter, id }) {
    return this.get(`tickets/${id}`, { params: { headquarter } });
  }

  // POST /tickets -> 201 { id }
  async crearTicket({ headquarter, ...body }) {
    return this.post('tickets', { headquarter, ...body });
  }

  // PATCH /tickets/:id -> 200 { message, cambios: [...] }
  async actualizarTicket({ headquarter, id, ...cambios }) {
    return this.patch(`tickets/${id}`, { headquarter, ...cambios });
  }

  /* ---- Comentarios (conversacion) ---- */

  // GET /tickets/:id/comentarios -> [...]
  async listarComentarios({ headquarter, id, soloPublicos }) {
    return this.get(`tickets/${id}/comentarios`, {
      params: { headquarter, solo_publicos: soloPublicos },
    });
  }

  // POST /tickets/:id/comentarios -> 201 { id }
  // agente se resuelve del token en el backend si no se envia.
  async crearComentario({ headquarter, id, ...body }) {
    return this.post(`tickets/${id}/comentarios`, { headquarter, ...body });
  }

  /* ---- CC del ticket ---- */

  // PUT /tickets/:id/cc -> 200 { cc: [emails] }. Sustituye la lista entera ([] la
  // vacia); las respuestas publicas salen con ellos en copia. silent: los 400
  // (email invalido, solicitante, buzon de soporte) se muestran junto al campo.
  async actualizarCc({ headquarter, id, emails }) {
    return this.put(`tickets/${id}/cc`, { headquarter, emails }, { silent: true });
  }

  /* ---- Etiquetas del ticket ---- */

  // POST /tickets/:id/etiquetas -> 200 { etiqueta_id }
  async agregarEtiqueta({ headquarter, id, etiquetaId, nombre }) {
    return this.post(`tickets/${id}/etiquetas`, {
      headquarter,
      ...(etiquetaId != null ? { etiqueta_id: etiquetaId } : {}),
      ...(nombre != null ? { nombre } : {}),
    });
  }

  // DELETE /tickets/:id/etiquetas/:etiquetaId -> 200
  async quitarEtiqueta({ headquarter, id, etiquetaId }) {
    return this.delete(`tickets/${id}/etiquetas/${etiquetaId}`, {
      params: { headquarter },
    });
  }

  /* ---- Adjuntos (Google Drive) ---- */

  // POST /tickets/:id/adjuntos -> 201 { id, ruta } (max ~3,7 MB por fichero)
  async subirAdjunto({ headquarter, id, ...body }) {
    return this.post(`tickets/${id}/adjuntos`, { headquarter, ...body });
  }

  // GET /tickets/:id/adjuntos -> [...]
  async listarAdjuntos({ headquarter, id }) {
    return this.get(`tickets/${id}/adjuntos`, { params: { headquarter } });
  }

  // DELETE /tickets/:id/adjuntos/:adjuntoId -> 200
  async eliminarAdjunto({ headquarter, id, adjuntoId }) {
    return this.delete(`tickets/${id}/adjuntos/${adjuntoId}`, {
      params: { headquarter },
    });
  }

  /* ---- Historial (auditoria) ---- */

  // GET /tickets/:id/historial -> [...]
  async obtenerHistorial({ headquarter, id }) {
    return this.get(`tickets/${id}/historial`, { params: { headquarter } });
  }

  /* ---- Informes (actividad) ---- */

  // GET /tickets/informe/filtros -> { bandejas, prioridades, canales, temas, estados, agentes, clientes }
  async listarFiltrosInforme({ headquarter }) {
    return this.get('tickets/informe/filtros', { params: { headquarter } });
  }

  // GET /tickets/informe/actividad -> { rango, resumen, series }
  // filtros opcionales: grupo_id, prioridad, solicitante_id, canal, tema_id, agente_id, estado_id
  async obtenerInformeActividad({ headquarter, ...filtros }) {
    return this.get('tickets/informe/actividad', { params: { headquarter, ...filtros } });
  }

  // GET /tickets/informe/actividad/pdf -> application/pdf (blob)
  async descargarInformeActividadPdf({ headquarter, ...filtros }) {
    return this.get('tickets/informe/actividad/pdf', {
      params: { headquarter, ...filtros },
      responseType: 'blob',
    });
  }

  /* ---- Respuestas guardadas (macros) ---- */

  // GET /tickets/respuestas -> [{ id, titulo, cuerpo, creadoPor, fechaAlta }]
  async listarRespuestas({ headquarter }) {
    return this.get('tickets/respuestas', { params: { headquarter } });
  }

  // POST /tickets/respuestas -> 201 { id }. Solo autorizados (403 al resto);
  // silent para mostrar el mensaje del backend en la UI en vez del toast global.
  async crearRespuesta({ headquarter, titulo, cuerpo }) {
    return this.post(
      'tickets/respuestas',
      { headquarter, titulo, cuerpo },
      { params: { headquarter }, silent: true },
    );
  }

  // PUT /tickets/respuestas/:id -> { ok: true }. silent por el mismo motivo.
  async actualizarRespuesta({ headquarter, id, titulo, cuerpo }) {
    return this.put(
      `tickets/respuestas/${id}`,
      { headquarter, titulo, cuerpo },
      { params: { headquarter }, silent: true },
    );
  }

  // DELETE /tickets/respuestas/:id -> { ok: true }. silent por el mismo motivo.
  async eliminarRespuesta({ headquarter, id }) {
    return this.delete(`tickets/respuestas/${id}`, {
      params: { headquarter },
      silent: true,
    });
  }

  /* ---- Bloqueos de correo entrante ---- */

  // GET /tickets/bloqueos -> [{ id, tipo: 'asunto'|'remitente', patron, motivo, creadoPor, fechaAlta }]
  async listarBloqueos({ headquarter }) {
    return this.get('tickets/bloqueos', { params: { headquarter } });
  }

  // POST /tickets/bloqueos -> 201 { id }. 403 a no autorizados, 409 si ya existe;
  // silent para mostrar el mensaje del backend en la UI en vez del toast global.
  async crearBloqueo({ headquarter, tipo, patron, motivo }) {
    return this.post(
      'tickets/bloqueos',
      { headquarter, tipo, patron, motivo },
      { params: { headquarter }, silent: true },
    );
  }

  // DELETE /tickets/bloqueos/:id -> { ok: true }. silent por el mismo motivo.
  async eliminarBloqueo({ headquarter, id }) {
    return this.delete(`tickets/bloqueos/${id}`, {
      params: { headquarter },
      silent: true,
    });
  }

  /* ---- Borrador del agente (autosave por ticket) ---- */

  // GET /tickets/:id/borrador -> { cuerpo, actualizado_en, creado_en } cuando hay
  // borrador, o { cuerpo: null } si el agente no tiene ninguno (no es error).
  async obtenerBorrador({ headquarter, id }) {
    return this.get(`tickets/${id}/borrador`, { params: { headquarter } });
  }

  // PUT /tickets/:id/borrador -> 200 { message, cuerpo, actualizado_en }. Upsert
  // (una fila por agente+ticket). OJO: el headquarter va en el BODY, no en query.
  // silent: se guarda en segundo plano mientras el agente escribe; un fallo no
  // debe disparar el toast global ni interrumpir la escritura.
  async guardarBorrador({ headquarter, id, cuerpo }) {
    return this.put(`tickets/${id}/borrador`, { headquarter, cuerpo }, { silent: true });
  }

  // DELETE /tickets/:id/borrador -> 200 { message }. Idempotente (200 aunque no
  // existiera). silent por el mismo motivo que el PUT.
  async descartarBorrador({ headquarter, id }) {
    return this.delete(`tickets/${id}/borrador`, { params: { headquarter }, silent: true });
  }
}

const ticketsService = new TicketsService(config.REACT_APP_NODE_API);
export default ticketsService;
